import { fabric } from 'fabric'
import { v4 as guid } from 'uuid'
import { pick } from 'lodash-es'
import type DuneAsset from '@/models/duneAsset'
import appConfig from '@/services/appConfig'
import pngLoading from '@/assets/loading.png'
import pngNoImage from '@/assets/noimg.png'
import { merchConstants } from '@/models/constants'
import { useUserStore } from '@/store/userData'
import { getArticleAssets } from '@/api/t1/article'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'

interface IMbModelImageOptions extends fabric.IImageOptions {
  id?: string
  modelId: number
  articleId: number
  article?: MyArticle
  locked?: boolean
  templateObject?: boolean
  imageType?: string | null
}

const editableProps: Record<string, IMbObjectProp> = {
  imageType: { name: 'image type', type: 'imageType' },
  scale: { name: 'size', type: 'scale' },
  locked: { name: 'lock', type: 'lock' },
  addModelDetails: { name: 'add model details', type: 'addModelDetails' },
}

export default class MbModelImage extends fabric.Image implements IMbObject {
  public id: string
  public modelId: number
  public articleId: number
  public article?: MyArticle
  public assets: DuneAsset[]
  public type = merchConstants.objectTypes.modelImage
  public locked: boolean
  public imgLoaded: boolean
  public imgLoading: boolean
  public templateObject: boolean
  public imageType: string | null

  public editableProps: Record<string, IMbObjectProp> = editableProps

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    copy: { action: 'copy', label: 'Copy Selection', faicon: 'fa-light fa-copy', showInSubMenu: true },
    align: { action: 'align', label: 'Align', faicon: 'fa-light fa-align-justify', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  constructor(element: string | HTMLImageElement | HTMLCanvasElement | HTMLVideoElement, options: IMbModelImageOptions, imageScaleFactor?: number, defaultImageType?: string) {
    super(element, options)
    this.id = options.id || guid()
    this.modelId = options.modelId
    this.articleId = options.articleId
    this.article = options.article
    this.assets = []
    this.locked = options.locked || false
    this.imageType = options.imageType || defaultImageType || null
    this.imgLoaded = false
    this.imgLoading = false
    this.templateObject = options.templateObject || false
    this.originX = 'center'
    this.originY = 'center'
    const scaleFactorX = imageScaleFactor || merchConstants.slideImageDefaultScaleFactors.large
    const scaleFactorY = imageScaleFactor || merchConstants.slideImageDefaultScaleFactors.large
    this.scaleX = options.scaleX || scaleFactorX
    this.scaleY = options.scaleY || scaleFactorY
    // if (isLiveObject) {
    //   const userStore = useUserStore()
    //   const observable = liveQuery(async () => appConfig.DB!.getArticlesByCatalogCodeModelNumbers(userStore.activeCatalog!, [this.modelNumber], userStore.currentCustomer, userStore.currentCustomerSegmentations).then(collection => collection.toArray()))
    //   const subscription = observable.subscribe((modelArticles) => {
    //     // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
    //     if (!this.isLoadingFirstTime) {
    //       if (modelArticles && modelArticles.length && modelArticles.some(article => article.Status === 1)) {
    //         if (this.opacity !== 1) {
    //           this.setProp('opacity', { opacity: 1 }, true)
    //         }
    //       }
    //       else if (this.opacity !== 0.5) {
    //         this.setProp('opacity', { opacity: 0.5 }, true)
    //       }
    //     }
    //     else {
    //       this.isLoadingFirstTime = false
    //     }
    //   })
    //   this.subscriptions.push(subscription)
    // }

    this.setLock(this.locked)

    this.stateProperties = this.stateProperties?.concat(['locked'])
  }

  setProp(prop: string, value: any, ignoreHistory: boolean = false) {
    switch (prop) {
      case 'imageType':
        this.set('imageType', value.assetKey)
        this.imgLoaded = false
        this.imgLoading = false
        break
      case 'scale':
        if (value.scale <= 100 && this.width && this.height) {
          const scale = value.scale / 100
          this.set('scaleX', scale)
          this.set('scaleY', scale)
        }
        break
      case 'locked':
        this.set('locked', value.locked)
        this.setLock(value.locked)
        break
      case 'opacity':
        this.set('opacity', value.opacity)
        break
      default:
        console.warn('attempting to set unsupported MbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this, ignoreHistory })
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'imageType':
        result.assetKey = this.imageType
        break
      case 'scale':
        result.scale = Math.round((this.scaleX || 1) * 100)
        break
      case 'locked':
        result.lock = this.locked
        break
      default:
        console.warn('attempting to get unsupported MbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'width',
      'height',
      'modelId',
      'articleId',
      'angle',
      'scaleX',
      'scaleY',
      'locked',
      'templateObject',
      'imageType',
    ]
    return pick(this, propsToPluck)
  }

  // static fromObject(object: MbModelImage, callback?: Function) {
  //   this.loadModelImageByModelId(object.modelId, object.width || 500, object.height || 500, object).then((v) => { callback && callback(v) })
  // }

  static getImageSrc(width: number, height: number, assets?: DuneAsset[], imageType?: string | null) {
    let src = pngNoImage
    const userStore = useUserStore()
    if (userStore && userStore.activeCatalog && assets && assets.length > 0) {
      // in case of modelImage alway show the first image of the model article
      let asset = assets[0]
      // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
      if (utils.isValidStringValue(imageType)) {
        const selectedAsset = assets.find(asset => asset.Key === imageType)
        if (selectedAsset) {
          asset = selectedAsset
        }
      }
      src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(userStore.activeCatalog.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
    }
    return src
  }

  static async loadModelImageByModelId(modelId: number, options: IMbModelImageOptions, imageSize: number) {
    if (options.article) {
      return await this.loadModelImage(options.article, options, imageSize)
    }
    else if (modelId) {
      const userStore = useUserStore()
      if (appConfig.DB && userStore.activeCatalog && userStore.myAttributes) {
        const articles = await appConfig.DB.getFirstActiveMyArticleByModelIds([modelId], userStore.activeCatalog, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.currentCustomer, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, [], true)
        if (articles && articles.length) {
          return await this.loadModelImage(articles[0], options, imageSize)
        }
      }
    }
    console.warn('Failed to load model image by modelId', options)
    return null
  }

  static loadModelImage(article: MyArticle, options?: IMbModelImageOptions, imageSize?: number) {
    return new Promise<MbModelImage>((resolve) => {
      fabric.util.loadImage(pngLoading, async (img) => {
        const userStore = useUserStore()
        const width = options?.width || 500
        const height = options?.height || 500
        const scaleFactor = imageSize || merchConstants.slideImageDefaultScaleFactors.large
        const currentOptions = Object.assign((options || { width, height }), { articleId: article.Id, modelId: article.ModelId, article })
        const modelImageObject = new MbModelImage(img, currentOptions, scaleFactor)
        modelImageObject.article = article
        modelImageObject.modelId = article.ModelId
        modelImageObject.articleId = article.Id

        const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog
        modelImageObject.imgLoading = true
        getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ModelNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList, false)
          .then((assets) => {
            modelImageObject.assets = assets
            if (!modelImageObject.imageType && assets.length) {
              modelImageObject.imageType = assets[0].Id
              if (utils.isValidStringValue(userStore.activeCatalog?.Config.DefaultImageTypeForModel)) {
                const configuredKeyAssetAvailable = modelImageObject.assets.find(asset => asset.Key.toLowerCase() === userStore.activeCatalog!.Config.DefaultImageTypeForModel.toLowerCase())
                modelImageObject.imageType = configuredKeyAssetAvailable ? configuredKeyAssetAvailable.Id : assets[0].Id
              }
            }
            modelImageObject.canvas?.requestRenderAll()
          })
          .finally(() => {
            modelImageObject.imgLoaded = false
            modelImageObject.imgLoading = false
          })

        resolve(modelImageObject)
      })
    })
  }

  // static async getJsonObject(article: MyArticle, width: number, height: number, scaleValue, opt?: IMbModelImageOptions) {
  //   const userStore = useUserStore()
  //   const o = Object.assign((opt || { width, height }), { catalogCode: article.CatalogCode, modelId: article.ModelId, articleId: article.Id })
  //   const catalogDetails = userStore.linkedCatalogDetails[article.CatalogCode] || userStore.activeCatalog

  //   const obj: any = new MbModelImage('', o).toObject()
  //   if (scaleValue.scale <= 100 && width && height) {
  //     const scale = scaleValue.scale / 100
  //     obj.scaleX = scale
  //     obj.scaleY = scale
  //   }
  //   const modelAssets = await getArticleAssets(catalogDetails.DuneContext, catalogDetails.ContextKey, article.ModelNumber, userStore.activeCatalog?.Config.NewestImageAssetKeyList)
  //   if (opt && modelAssets && modelAssets.length > 0 && catalogDetails) {
  //     let asset = modelAssets[0]

  //     if (opt && opt.assetKey === 'firstImage') {
  //       const assetsClone = clone(modelAssets)
  //       utils.sort(assetsClone, ['SortOrder'])
  //       asset = assetsClone[0]
  //     }
  //     else if (opt.assetKey === 'newestImage') {
  //       asset = article._Assets[0]
  //     }
  //     else {
  //       // if requested for specific assetKey then search in _Assets, if does not exist in _Assets it should show placeholder
  //       if (utils.isValidStringValue(opt.assetKey)) {
  //         const selectedAsset = modelAssets.find(asset => asset.Id === opt.assetKey || asset.Key === opt.assetKey)
  //         if (selectedAsset) {
  //           asset = selectedAsset
  //         }
  //       }
  //     }
  //     obj.src = `${appConfig.AssetsUrl}/assets/content/${asset.StorageFile}?ContextKey=${encodeURIComponent(catalogDetails.ContextKey)}&f=webp&w=${width}&h=${height}&trim=true`
  //   }
  //   obj.height = height
  //   obj.width = width
  //   return obj
  // }

  static getEditableProps() {
    return editableProps
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx)
    if (!this.imgLoaded && !this.imgLoading) {
      if (this.assets && this.assets.length > 0) {
        this.imgLoading = true
        const src = MbModelImage.getImageSrc(this.width || 500, this.height || 500, this.assets, this.imageType)
        this.setSrc(src, (img, error) => {
          if (error) {
            this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
            console.warn('unable to load MbModelImage', this, error)
          }
          else {
            this.canvas?.requestRenderAll()
          }
          this.imgLoaded = true
          this.imgLoading = false
        })
      }
      else if (this.getSrc() !== pngNoImage) {
        this.setSrc(pngNoImage, () => this.canvas?.requestRenderAll())
      }
    }
  }
}

const f: any = fabric
f.MbModelImage = MbModelImage
